// src/lib/constants/appPlatform.ts
//
// The generic app platforms (one app_cfg_platform row each): Fire TV first;
// Google TV / Apple TV slot in as entries here. Roku and Web keep their own
// tables, routes and tabs and are never listed. There is no Android TV entry,
// ever: one Play listing serves both, so the product is the `googletv` row,
// labelled "Google TV (includes Android TV)", on the allowAndroidtv entitlement.

import { AppPlatformTypeId } from '@lib2/model/apps/AppPlatformType';
import type { FactoryStoreKey } from './appFactory';

/** The `{platform}` path segment of app/platform/* = the factory's platform key. */
export const APP_PLATFORM_KEYS = ['firetv'] as const;
export type AppPlatformKey = typeof APP_PLATFORM_KEYS[number];

/** Which grammar the store package id follows. */
export type AppPlatformPackageKind = 'android' | 'apple';

export interface AppPlatformInfo {
  key: AppPlatformKey;
  typeId: AppPlatformTypeId;
  /** Tab + badge label. */
  label: string;
  /** "Fire TV app" — the noun the copy uses. */
  appNoun: string;
  /** The store this platform's binaries are submitted to. */
  store: FactoryStoreKey;
  packageKind: AppPlatformPackageKind;
  /** Operator field labels (the store console's own words). */
  storePackageIdLabel: string;
  storeAppIdLabel: string;
  storeAppIdHint: string;
}

export const APP_PLATFORM_INFO: Record<AppPlatformKey, AppPlatformInfo> = {
  firetv: {
    key: 'firetv',
    typeId: AppPlatformTypeId.Firetv,
    label: 'Fire TV',
    appNoun: 'Fire TV app',
    store: 'amazon',
    packageKind: 'android',
    storePackageIdLabel: 'Android package name',
    storeAppIdLabel: 'Amazon App ID',
    storeAppIdHint:
      'From the Amazon developer console, once the first version exists there (amzn1.devportal.mobileapp…)',
  },
};

export const APP_PLATFORM_CONSTRAINTS = {
  /** The launcher label; the API only requires it non-empty, the app name bounds the prefill. */
  TITLE_MAX: 150,
  STORE_PACKAGE_ID_MAX: 150,
  STORE_APP_ID_MAX: 200,
} as const;

/** The API's grammars (AppCfgPlatformRules) — also the factory's build admission rule. */
export const ANDROID_APPLICATION_ID_PATTERN = /^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)+$/;
export const APPLE_BUNDLE_ID_PATTERN = /^[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/;

/**
 * OFF until the App Factory API that knows the asset-set `platform` field is
 * live AND the api proxy forwards it: the factory refuses unknown request
 * fields, so sending it early fails every Roku image save. While off, the
 * asset-set calls never carry a platform (the factory reads that as Roku).
 */
export const FACTORY_ASSET_SET_PLATFORM_FIELD_ENABLED = false;
